// @ts-check

import { Component } from '@verndale/core';
import { createFocusTrap } from 'focus-trap';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { rovingIndex } from '@verndale/roving-ux';
import { KEYCODE } from '../../utils';

class Module extends Component {
  constructor(el) {
    super(el);
  }

  setupDefaults() {
    this.dom = {
      $header: this.el.closest('.header'),
      $trigger: this.el
        .closest('.header')
        .querySelector('.header-main__hamburger'),
      $categoryTriggers: this.el.querySelectorAll(
        '.primary-nav__category-trigger'
      ),
      $backButtons: this.el.querySelectorAll('.primary-nav__back .link'),
      $menuBoxes: this.el.querySelectorAll('.primary-nav__box'),
      $menuTriggers: this.el.querySelectorAll('.primary-nav__menu-trigger'),
      $grids: this.el.querySelectorAll('.primary-nav__grid')
    };

    const self = this;
    this.mainFocusTrap = createFocusTrap(this.dom.$header, {
      escapeDeactivates: true,
      initialFocus: self.dom.$trigger,
      onActivate() {
        document.body.style.inlineSize = '100%';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        disableBodyScroll(self.el, {
          // @ts-ignore
          allowTouchMove: (elem) => elem.closest('.header')
        });
      },
      onDeactivate() {
        clearAllBodyScrollLocks();
        document.body.style.inlineSize = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
      }
    });

    this.menuFocusTraps = Array.from(this.dom.$menuBoxes).map((box) =>
      createFocusTrap(box, {
        escapeDeactivates: true,
        clickOutsideDeactivates: true
      })
    );
    this.setBoxInlineSize();
    this.setRovings();
  }

  addListeners() {
    this.dom.$header.addEventListener('keydown', (e) =>
      this.handleHeaderKeydown(e)
    );
    this.dom.$trigger.addEventListener('click', () => this.toggleNav());
    this.dom.$categoryTriggers.forEach((trigger) =>
      trigger.addEventListener('click', (e) => this.toggleCategory(e))
    );
    this.dom.$menuTriggers.forEach((trigger, index) =>
      trigger.addEventListener('click', (e) => this.toggleMenu(e, index))
    );
    this.dom.$menuBoxes.forEach((box, index) =>
      box.addEventListener('keydown', (e) => this.handleBoxKeydown(e, index))
    );
    this.dom.$menuBoxes.forEach((box, index) => {
      box.addEventListener('keydown', (e) => this.handleBoxKeydown(e, index));
      box.addEventListener('focusout', (e) => this.handleBoxFocusout(e, index));
    });
    this.dom.$backButtons.forEach((button, index) =>
      button.addEventListener('click', (e) => this.handleBackButton(e, index))
    );
  }

  handleHeaderKeydown(e) {
    if (e.keyCode === KEYCODE.ESCAPE) {
      e.stopPropagation();
      this.deactivateMain();
    }
  }

  handleBoxKeydown(e, index) {
    if (e.keyCode === KEYCODE.ESCAPE) {
      e.stopPropagation();
      const $trigger = e.currentTarget.previousElementSibling;
      this.deactivateMenu($trigger, e.currentTarget, index);
    }
  }

  handleBoxFocusout(e, index) {
    const $trigger = e.currentTarget.previousElementSibling;

    if (
      !e.currentTarget.contains(e.relatedTarget) &&
      e.relatedTarget !== $trigger
    ) {
      this.deactivateMenu($trigger, e.currentTarget, index);
    }
  }

  handleBackButton(e, index) {
    const $box = e.currentTarget.closest('.primary-nav__box');
    const $trigger = $box.previousElementSibling;
    this.deactivateMenu($trigger, $box, index);
  }

  deactivateMain() {
    this.dom.$header.dataset.menuExpanded = false;
    this.dom.$trigger.setAttribute('aria-expanded', false);
    this.dom.$menuTriggers.forEach((trigger) =>
      trigger.setAttribute('aria-expanded', false)
    );
    this.menuFocusTraps.forEach((trap) => trap.deactivate());
    const cb = (e) => {
      if (e.propertyName === 'opacity') {
        this.mainFocusTrap.deactivate();
        this.el.removeEventListener('transitionend', cb);
      }
    };
    this.el.addEventListener('transitionend', cb);
  }

  activateMain() {
    this.dom.$header.dataset.menuExpanded = true;
    this.dom.$trigger.setAttribute('aria-expanded', true);
    const cb = (e) => {
      if (e.propertyName === 'opacity') {
        this.mainFocusTrap.activate();
        this.el.removeEventListener('transitionend', cb);
      }
    };
    this.el.addEventListener('transitionend', cb);
  }

  toggleCategory(e) {
    const isExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';
    const $triggerContainer = e.currentTarget.closest(
      '.primary-nav__category-trigger-container'
    );

    e.currentTarget.setAttribute('aria-expanded', !isExpanded);
    $triggerContainer.dataset.open = !isExpanded;
  }

  toggleNav() {
    const isExpanded = this.dom.$header.dataset.menuExpanded === 'true';

    if (isExpanded) this.deactivateMain();
    else this.activateMain();
  }

  toggleMenu(e, index) {
    const isExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';
    const $box = e.currentTarget.nextElementSibling;

    if (isExpanded) this.deactivateMenu(e.currentTarget, $box, index);
    else this.activateMenu(e.currentTarget, $box, index);
  }

  activateMenu($trigger, $box, index) {
    $trigger.setAttribute('aria-expanded', true);

    const cb = (e) => {
      if (e.propertyName === 'opacity') {
        this.menuFocusTraps[index]?.activate?.();
        $box.removeEventListener('transitionend', cb);
      }
    };

    $box.addEventListener('transitionend', cb);
  }

  deactivateMenu($trigger, $box, index) {
    $trigger.setAttribute('aria-expanded', false);

    const cb = (e) => {
      if (e.propertyName === 'opacity') {
        this.menuFocusTraps[index]?.deactivate();
        $box.removeEventListener('transitionend', cb);
      }
    };

    $box.addEventListener('transitionend', cb);
  }

  setBoxInlineSize() {
    this.dom.$menuBoxes.forEach((box) => {
      const $cols = box.querySelectorAll('.primary-nav__grid-col');
      box.classList.toggle('primary-nav__box--partial', $cols.length < 4);
    });
  }

  setRovings() {
    this.dom.$grids.forEach((grid) => {
      const target = grid.querySelector('.primary-nav__category-trigger');
      if (target) {
        rovingIndex({
          element: grid,
          target: '.primary-nav__category-trigger'
        });
      }
    });
  }
}

export default Module;
